/**
 * ACA Amend Application Fields
 * This is an isolated configuration for ACA amend applications
 * Based on the original ACA application fields but with amend-specific modifications
 */

import { RuleProps } from 'types/application';
import { isFieldDisabledInAmend } from 'features/applicationPage/helpers/amendFieldControl';

export const SECT_COMPANY_DETAILS_AMEND = 'company-details-amend';
export const SECT_FILE_ATTACHMENTS_AMEND = 'file-attachments-amend';
export const SECT_OWNERSHIP_AMEND = 'ownership-amend';
export const SECT_FINANCIAL_DETAILS_AMEND = 'financial-details-amend';
export const SECT_FUNDING_CREDIT_INFORMATION_AMEND = 'funding-credit-information-amend';
export const SECT_BUSINESS_PLANS_AMEND = 'business-plans-amend';
export const SECT_PERSONNEL_AMEND = 'personnel-amend';
export const SECT_INFORMATION_INSOLVENCY_AMEND = 'information-insolvency-amend';

// Import the original ACA fields to extend from
import { ACA_APPLICATION_FIELDS } from './acaApplicationDetails';

/**
 * ACA Amend Application Fields
 * These are the same as ACA_APPLICATION_FIELDS but with amend-specific section IDs
 * and disabled rules based on amend type
 */
export const ACA_AMEND_APPLICATION_FIELDS = ACA_APPLICATION_FIELDS.map(section => {
    // Create a new section with amend-specific ID
    const amendSectionId = section.sectionID + '-amend';
    
    return {
        ...section,
        sectionID: amendSectionId,
        fieldList: section.fieldList.map(field => {
            // Get existing rule or create empty object
            const existingRule = field.rule || {};
            
            // Get existing disabled function if it exists - use type assertion for safety
            const existingDisabled = (existingRule as any).disabled as boolean | ((props: RuleProps) => boolean) | undefined;
            
            // Create new disabled function that combines existing logic with amend logic
            const newDisabled = (props: RuleProps) => {
                const { amendType, isAmendMode } = props;
                
                // First check existing disabled logic
                if (existingDisabled !== undefined) {
                    if (typeof existingDisabled === 'boolean') {
                        if (existingDisabled) return true;
                    } else if (typeof existingDisabled === 'function') {
                        const isDisabledByExisting = existingDisabled(props);
                        if (isDisabledByExisting) return true;
                    }
                }
                
                // Then check amend-specific disabled logic
                return isFieldDisabledInAmend(amendSectionId, amendType || null, isAmendMode || false);
            };
            
            // Update baseIds to use amend section IDs - use JSON serialization for safe cloning
            const updatedRule = JSON.parse(JSON.stringify(existingRule));
            if (updatedRule.baseIds) {
                updatedRule.baseIds = updatedRule.baseIds.map((baseId: string) => {
                    // Replace original section IDs with amend versions
                    return baseId.replace(/^(company-details|ownership|financial-details|funding-credit-information|business-plans|personnel|information-insolvency)/, '$1-amend');
                });
            }
            
            // Handle conditional field logic - update all conditional fields to use amend section IDs
            if (field.id === 'registered-as-others') {
                updatedRule.required = ({ baseValues }: RuleProps) => {
                    const result = Boolean(baseValues && baseValues[`${amendSectionId}-registered-as`] === 'CAOT');
                    return result;
                };
                updatedRule.hidden = ({ baseValues }: RuleProps) => {
                    const result = Boolean(baseValues && baseValues[`${amendSectionId}-registered-as`] !== 'CAOT');
                    return result;
                };
            }
            
            // Handle insolvency detail fields
            if (field.id === 'involved-legal-details') {
                updatedRule.required = ({ baseValues }: RuleProps) => {
                    const result = Boolean(baseValues && baseValues[`information-insolvency-amend-involved-legal`] === 'Yes');
                    return result;
                };
                updatedRule.hidden = ({ baseValues }: RuleProps) => {
                    const result = Boolean(baseValues && baseValues[`information-insolvency-amend-involved-legal`] !== 'Yes');
                    return result;
                };
            }
            
            if (field.id === 'substantial-shareholder-details') {
                updatedRule.required = ({ baseValues }: RuleProps) => {
                    const result = Boolean(baseValues && baseValues[`information-insolvency-amend-substantial-shareholder`] === 'Yes');
                    return result;
                };
                updatedRule.hidden = ({ baseValues }: RuleProps) => {
                    const result = Boolean(baseValues && baseValues[`information-insolvency-amend-substantial-shareholder`] !== 'Yes');
                    return result;
                };
            }
            
            if (field.id === 'receiver-manager-shareholder-details') {
                updatedRule.required = ({ baseValues }: RuleProps) => {
                    const result = Boolean(baseValues && baseValues[`information-insolvency-amend-receiver-manager-shareholder`] === 'Yes');
                    return result;
                };
                updatedRule.hidden = ({ baseValues }: RuleProps) => {
                    const result = Boolean(baseValues && baseValues[`information-insolvency-amend-receiver-manager-shareholder`] !== 'Yes');
                    return result;
                };
            }
            
            if (field.id === 'compromise-creditors-details') {
                updatedRule.required = ({ baseValues }: RuleProps) => {
                    const result = Boolean(baseValues && baseValues[`information-insolvency-amend-compromise-creditors`] === 'Yes');
                    return result;
                };
                updatedRule.hidden = ({ baseValues }: RuleProps) => {
                    const result = Boolean(baseValues && baseValues[`information-insolvency-amend-compromise-creditors`] !== 'Yes');
                    return result;
                };
            }
            
            if (field.id === 'fraudulent-dishonest-details') {
                updatedRule.required = ({ baseValues }: RuleProps) => {
                    const result = Boolean(baseValues && baseValues[`information-insolvency-amend-fraudulent-dishonest`] === 'Yes');
                    return result;
                };
                updatedRule.hidden = ({ baseValues }: RuleProps) => {
                    const result = Boolean(baseValues && baseValues[`information-insolvency-amend-fraudulent-dishonest`] !== 'Yes');
                    return result;
                };
            }
            
            return {
                ...field,
                rule: {
                    ...updatedRule,
                    disabled: newDisabled
                }
            };
        })
    };
});

// Export individual sections for easy reference
export const MATERIAL_FIELD_SECTIONS_ACA = [
    SECT_COMPANY_DETAILS_AMEND,
    SECT_OWNERSHIP_AMEND,
    SECT_FINANCIAL_DETAILS_AMEND,
    SECT_FUNDING_CREDIT_INFORMATION_AMEND,
    SECT_BUSINESS_PLANS_AMEND,
    SECT_PERSONNEL_AMEND,
    SECT_INFORMATION_INSOLVENCY_AMEND,
    SECT_FILE_ATTACHMENTS_AMEND
];

export const NON_MATERIAL_FIELD_SECTIONS_ACA = [
    // Sections within the "general-amend" form (Applicant/Filer Details)
    'applicant-type-section',
    'filer-details', 
    'applicant-details',
    // Sections within the "contact-billing-details-amend" form (Billing Details)
    'contact-particulars',
    'billing-details',
];
